<script setup lang="ts">
import { useTranslations } from '@/composables/useTranslations.ts'
import { computed } from 'vue'
import { isNull, isNil } from 'lodash'
import type { ChatTrackingInfo } from '@/components/chat/interfaces/chat.interfaces.ts'
import copy from 'copy-to-clipboard'
import { useToast } from 'vue-toastification'
import { faCopy } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { StopCircleIcon } from '@heroicons/vue/24/solid'

const toast = useToast()
const { t } = useTranslations()
const props = defineProps<{
  info: ChatTrackingInfo | null | undefined
  contactEmails: string | null | undefined
  contactPhones: string | null | undefined
  isAiControlIntercepted: boolean | undefined
}>()

const emit = defineEmits(['onStopAiClicked'])

const region = computed(() => {
  const { info } = props
  let regionName = ''

  if (!isNil(info) && info.location) {
    if (!isNull(info.location.country)) {
      regionName = info.location.country
    }
    if (!isNull(info.location.region) && info.location.country !== info.location.region) {
      regionName = `${regionName}, ${info.location.region}`
    }
    if (
      !isNull(info.location.city) &&
      info.location.country !== info.location.region &&
      info.location.region !== info.location.city
    ) {
      regionName = `${regionName}, ${info.location.city}`
    }
  }

  return regionName || 'N/A'
})

// TODO: return back after backend will be ready
// const tags = ref<string[]>([])
// const newTag = ref<string>('')
//
// function addTag() {
//   if (newTag.value.trim()) {
//     tags.value.push(newTag.value.trim())
//     newTag.value = ''
//   }
// }

// function removeTag(index: number) {
//   tags.value.splice(index, 1)
// }

function onEnvelopeIconDivWrapperClicked(ev: Event, emails: string) {
  ev.preventDefault()
  copy(emails)
  toast.success('Copied!')
}

function onPhoneIconDivWrapperClicked(ev: Event, phones: string) {
  ev.preventDefault()
  copy(phones)
  toast.success('Copied!')
}
</script>

<template>
  <div class="flex xl:flex-col flex-row p-4 sm:items-end bg-white shadow-md rounded-lg gap-4">
    <!-- Details Section -->
    <div class="md:mb-0 max-md:m-auto w-full">
      <p class="text-xs md:text-sm md:pl-2 font-semibold text-center">{{ t('chats.customerDetails.title') }}</p>
      <div
        v-if="contactEmails || contactPhones"
        class="flex flex-col pl-4 md:flex-row md:items-center xl:flex-col xl:items-start bg-gray-100 p-1 max-sm:p-3 my-2 rounded-lg text-sm text-gray-800 shadow-md gap-2"
      >
        <div
          v-if="!isNull(contactEmails)"
          v-tooltip:left="t('chat.customerDetails.emailTooltip')"
          @click="(ev) => onEnvelopeIconDivWrapperClicked(ev, contactEmails as string)"
        >
          <div>{{ t('chats.customerDetails.email') }}:</div>
          <font-awesome-icon :icon="faCopy" class="size-4 max-sm:size-5" />
          <span class="pl-2">{{ contactEmails }}</span>
        </div>
        <div
          v-if="!isNull(contactPhones)"
          v-tooltip:left="t('chat.customerDetails.phoneTooltip')"
          @click="(ev) => onPhoneIconDivWrapperClicked(ev, contactPhones as string)"
        >
          <div>{{ t('chats.customerDetails.phone') }}:</div>
          <font-awesome-icon :icon="faCopy" class="size-4 max-sm:size-5" />
          <span class="pl-2"> {{ contactPhones }}</span>
        </div>
      </div>

      <div
        class="bg-gray-100 p-3 mt-1 break-words rounded-lg text-gray-800 shadow-md text-sm/tight md:text-md"
      >
        <p>{{ t('chats.trackingInfo.referer') }}: {{ info?.referer || 'N/A' }}</p>
        <p>{{ t('chats.trackingInfo.screenResolution') }}: {{ info?.screenResolution || 'N/A' }}</p>
        <p>{{ t('chats.trackingInfo.clientIp') }}: {{ info?.clientIp || 'N/A' }}</p>
        <p>{{ t('chats.trackingInfo.platform') }}: {{ info?.platform || 'N/A' }}</p>
        <p>{{ t('chats.trackingInfo.browserLanguage') }}: {{ info?.browserLanguage || 'N/A' }}</p>
        <!--<p>{{ t('chats.trackingInfo.region') }}: {{ region }}</p>-->
      </div>
    </div>
    <!-- Tags Section: TODO return back after backend will be ready -->
    <!-- <div class="mb-4 shadow-md p-4 pt-1 rounded-lg bg-gray-100">
      <p class="text-md mb-2">Tags</p>
      <div class="flex flex-wrap gap-2">
        <div v-for="(tag, index) in tags" :key="index">
          <span class="bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm flex items-center">
            {{ tag }}
            <button class="ml-2 text-gray-500 hover:text-gray-700" @click="() => removeTag(index)">
              x
            </button>
          </span>
        </div>
        <div class="flex gap-2 mt-2">
          <input
            type="text"
            v-model="newTag"
            @keyup.enter="addTag"
            placeholder="Add a tag"
            class="border border-gray-300 rounded px-2 py-1 w-full"
          />
          <button
            @click="addTag"
            class="bg-purpleCustom text-white px-4 py-1 rounded hover:bg-indigo-400"
          >
            Add
          </button>
        </div>
      </div>
    </div> -->

    <!-- Ai control intercepted section -->
    <!-- Toggle AI -->
    <div
      class="max-md:items-start justify-center max-sm:hidden flex flex-col p-4 bg-white shadow-lg rounded-lg md:justify-between"
    >
      <div>
        <div class="flex-col text-xs md:text-sm font-semibold text-center pb-2">{{ t('chats.page.aiControl') }}</div>
        <span class="text-xs text-gray-500">
          {{ t('chats.page.controlIsCurrently') }}:
          {{ isAiControlIntercepted ? t('chats.page.disabled') : t('chats.page.enabled') }}</span
        >
        <div class="text-xs text-gray-500 max-xl:hidden">
          {{ t('chats.page.toWriteMessagesManuallyAiControlShouldBeDisabled') }}
        </div>
      </div>

      <div class="items-center flex flex-row md:flex-col pt-2 gap-2">
        <button
          :disabled="Boolean(isAiControlIntercepted)"
          @click="emit('onStopAiClicked')"
          class="rounded-lg px-4 py-2 w-auto items-center flex justify-center"
          :class="[
            isAiControlIntercepted
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-fuchsiaCustom text-white hover:bg-red-400',
          ]"
        >
          <StopCircleIcon class="w-5 h-5 mr-1" />
          {{ t('chats.page.stopAi') }}
        </button>
      </div>
    </div>
  </div>
</template>
